<template>
    <div class="photo-container" ref="container">
      <div
        v-for="(photo, index) in folders"
        :key="photo.name"
        class="photo"
        :style="getPhotoStyle(index)"
      >
        <!-- Replace with your photo markup -->
        <ImageGlow :src="'/figures/atm/' + photo.name + '-image.png'" />
      </div>
    </div>
  </template>
  
  <script>
  import ImageGlow from "./ImageGlow.vue"
  export default {
    name: "VerticalPhotoCarousel",
    components: {
      ImageGlow
    },
    props: {
      folders: { type: Array, required: true },
    },
    data() {
      return {
        // Global vertical offset (in pixels). Animated when setActiveFolder is called.
        globalOffset: 0,
        // Stub (visible part of the adjacent photos) in pixels.
        stub: window.innerWidth > 768 ? 24 : 0, // Set stub to 0 for mobile
        animationFrameId: null,
        // Animation state for centering.
        centeringAnimation: {
          active: false,
          startTime: 0,
          duration: 500, // milliseconds
          startOffset: 0,
          delta: 0,
        },
        // These values will be determined from the container.
        photoHeight: 150,      // Height of the active photo area.
        containerHeight: 198,  // Visible carousel height.
      };
    },
    computed: {
      // Total vertical cycle (each photo occupies a slot of height = photoHeight).
      totalHeight() {
        return this.folders.length * this.photoHeight;
      }
    },
    mounted() {
        // Adjust containerHeight based on the actual container size or window size for mobile
        this.containerHeight = Math.min(this.$refs.container.offsetHeight, window.innerHeight);
        // Compute photoHeight to ensure the active photo fills the space between the stubs
        this.photoHeight = Math.min(this.containerHeight - 2 * this.stub, this.containerHeight);
        this.startAnimationLoop();
        // Add event listener to update stub on window resize
        window.addEventListener('resize', this.updateStub);
    },
    beforeUnmount() {
        cancelAnimationFrame(this.animationFrameId);
        window.removeEventListener('resize', this.updateStub);
    },
    methods: {
      // Public method to center a given photo. Can be called via ref.
      setActiveFolder(activeIndex) {
        if (this.centeringAnimation.active) return;
        // We want the active photo to appear starting at y = stub.
        // The computed y for photo at index i is:
        //    y = (i * photoHeight) + stub - (globalOffset mod totalHeight)
        // So for the active photo, we need (activeIndex * photoHeight) - (globalOffset mod totalHeight) = 0.
        const idealOffset = activeIndex * this.photoHeight;
        // Choose the multiple of totalHeight that minimizes the jump.
        const k = Math.round((this.globalOffset - idealOffset) / this.totalHeight);
        const targetOffset = idealOffset + k * this.totalHeight;
        this.centeringAnimation = {
          active: true,
          startTime: performance.now(),
          duration: 500,
          startOffset: this.globalOffset,
          delta: targetOffset - this.globalOffset,
        };
      },
      // Compute the style for each photo.
      getPhotoStyle(index) {
        // Wrap the vertical offset using modulo arithmetic.
        let modOffset = ((this.globalOffset % this.totalHeight) + this.totalHeight) % this.totalHeight;
        let y = index * this.photoHeight + this.stub - modOffset;
        // Wrap upward: if the slot is too high, shift it down.
        if (y < -this.photoHeight) {
          y += this.totalHeight;
        }
        // Wrap downward: if the slot is below the container, shift it upward.
        if (y > this.containerHeight) {
          y -= this.totalHeight;
        }
        // return {
        //   position: "absolute",
        //   top: `${y}px`,
        //   height: `${this.photoHeight}px`,
        //   width: "300px",
        //   left: "50%",
        //   transform: "translateX(-50%)"
        // };
        return {
            position: "absolute",
            top: `${y}px`,
            height: `${this.photoHeight}px`,
            width: "100%", // Adjust width to be responsive
            left: "0", // Align to the left
            transform: "none" // Remove horizontal centering
        };
      },
      // Animation loop. When centering, animate globalOffset; otherwise, do nothing.
      startAnimationLoop() {
        let lastTime = performance.now();
        const animate = (currentTime) => {
          const deltaTime = currentTime - lastTime;
          lastTime = currentTime;
          if (this.centeringAnimation.active) {
            const elapsed = currentTime - this.centeringAnimation.startTime;
            const progress = Math.min(elapsed / this.centeringAnimation.duration, 1);
            // Ease-out quadratic easing.
            const easing = 1 - Math.pow(1 - progress, 2);
            this.globalOffset = this.centeringAnimation.startOffset + this.centeringAnimation.delta * easing;
            if (progress === 1) {
              this.centeringAnimation.active = false;
            }
          }
          this.animationFrameId = requestAnimationFrame(animate);
        };
        this.animationFrameId = requestAnimationFrame(animate);
      },
      updateStub() {
        // Keep stub at 24 for mobile to match the top spacing
        this.stub = window.innerWidth <= 768 ? 0 : 0;
        this.photoHeight = Math.min(this.containerHeight - 2 * this.stub, this.containerHeight);
      }
    }
  };
  </script>
  
  <style scoped>
  /* The container will fill its parent's height (which can be arbitrary)
     and the carousel content will be clipped if it overflows. */
  .photo-container {
    position: relative;
    overflow: hidden;
    width: 50%;
    height: 100%;
  }
  
  /* Each photo is absolutely positioned; the width is fixed to 300px and centered horizontally. */
  .photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  /* Limit the image width to 300px while allowing its height to adjust naturally. */
  .photo img {
    max-width: 300px;
    width: 100%;
    height: auto;
    display: block;
  }

  @media (max-width: 768px) {
    .photo-container {
      width: 80%;
      overflow: visible;
    }
  }
  </style>
  